import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import PortfolioOverview from '../components/PortfolioOverview';
import PinnedModuleSection from '../components/PinnedModuleSection';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import MaintenanceExposure from '../components/MaintenanceExposure';
import LesseeRisk from '../components/LesseeRisk';
import CreditInsurance from '../components/CreditInsurance';
import CompliancePanel from '../components/CompliancePanel';
import ReturnCondition from '../components/ReturnCondition';
import LeaseOptions from '../components/LeaseOptions';
import MREscalation from '../components/MREscalation';
import ShopVisitForecast from '../components/ShopVisitForecast';
import InvoiceCalendar from '../components/InvoiceCalendar';
import RemarketingPipeline from '../components/RemarketingPipeline';
import CustomAlerts from '../components/CustomAlerts';
import DepositTriggers from '../components/DepositTriggers';
import { getPinnedGraphsApi } from '../apiService';
import { getAssetListApi } from '../../leases/apiServices';
import { getMaintenanceChecksApi } from '../../analytics/apiService';
import { getCurrencyAc, getLesseeApi, getOwnerListApi } from '../../../shared_elements/actionCreators';
import { buildAnalyticsFilterMenu } from '../../analytics/analyticsFilterMenu';
import { PageLoader } from '../../../shared_elements';
import { trackActivity } from '../../../utils/mixpanel';
import DeploymentBanner from '../../../shared_elements/components/DeploymentBanner';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import '../styles/dashboard.scss';

const CURRENCY_OPTIONS_FALLBACK = [
  { id: 27, code: 'GBP' },
  { id: 3, code: 'EURO' },
  { id: 1, code: 'USD' },
];

const SECTIONS = [
  { key: 'portfolio', label: 'Portfolio Overview' },
  { key: 'utilization', label: 'Utilization' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'expiry', label: 'Expiry Pipeline' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'lessee', label: 'Lessee Risk' },
  { key: 'credit', label: 'Credit & Insurance' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'returncond', label: 'Return Condition' },
  { key: 'options', label: 'Lease Options' },
  { key: 'escalation', label: 'MR Escalation' },
  { key: 'svforecast', label: 'SV Forecast' },
  { key: 'invoicecal', label: 'Invoice Calendar' },
  { key: 'remarketing', label: 'Remarketing' },
  { key: 'alerts', label: 'Active Alerts' },
  { key: 'utilization_tracking', label: 'Utilization Tracking' },
  { key: 'sdtrigger', label: 'Deposit Triggers' },
  { key: 'portfolioCreditOps', label: 'Portfolio, Credit & Operational Intelligence' },
];

const initialVisible = SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {});

const TogglePill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`toggle-pill${active ? ' toggle-pill--active' : ''}`}
  >
    {label}
  </button>
);

const MODULE_UTILIZATION = 'Fleet Utilization Trends';
const MODULE_REVENUE = 'Revenue & Cashflow Performance';
const MODULE_EXPIRY = 'Lease Expiry';
const MODULE_CREDIT = 'Insurance Expiry';
const MODULE_ESCALATION = 'Upcoming Escalation Events';
const MODULE_ALERTS = 'Active Alerts';
const MODULE_UTILIZATION_TRACKING = 'Utilization Tracking';
const MODULE_MAINTENANCE = 'Maintenance Exposure';
const MODULE_LESSEE_RISK = 'Lessee Risk & Performance';
const MODULE_COMPLIANCE = 'Compliance & Contractual Obligations';
const MODULE_OPTIONS = 'Lease Options Pipeline';
const MODULE_INVOICE_CAL = 'Invoice Generation Calendar';
const MODULE_PORTFOLIO_CREDIT_OPS = 'Portfolio, Credit and Operational Intelligence';

function normalizePinnedByCategory(payload) {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }
  const out = {};
  for (const [category, list] of Object.entries(payload)) {
    out[category] = Array.isArray(list) ? list : [];
  }
  return out;
}

const FleetDashboard = () => {
  const dispatch = useDispatch();
  const { lessee, owner, currencies } = useSelector((state) => state.leases);
  const [visible, setVisible] = useState(initialVisible);
  const [pinnedByCategory, setPinnedByCategory] = useState({});
  const [pinnedListLoading, setPinnedListLoading] = useState(true);
  const [assetList, setAssetList] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  const currencyOptions =
    currencies?.length > 0 ? currencies : CURRENCY_OPTIONS_FALLBACK;

  const analyticsFilterMenu = useMemo(
    () =>
      buildAnalyticsFilterMenu(
        assetList,
        lessee || [],
        owner || [],
        currencyOptions,
        eventTypeOptions,
      ),
    [assetList, lessee, owner, currencyOptions, eventTypeOptions],
  );

  const toggle = (key) => setVisible(prev => ({ ...prev, [key]: !prev[key] }));

  const handleUnpin = useCallback((pinnedId) => {
    setPinnedByCategory((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = (next[key] || []).filter((p) => p.id !== pinnedId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    globalGetService(getAssetListApi()).then((response) => {
      if (checkApiStatus(response)) {
        setAssetList(response?.data?.data || []);
      }
    });
  }, []);

  useEffect(() => {
    globalGetService(getMaintenanceChecksApi()).then((response) => {
      if (checkApiStatus(response)) {
        setEventTypeOptions(response?.data?.data || []);
      }
    });
  }, []);

  useEffect(() => {
    if (!lessee?.length) dispatch(getLesseeApi());
    if (!owner?.length) dispatch(getOwnerListApi());
    if (!currencies?.length) dispatch(getCurrencyAc({}));
  }, [dispatch, lessee?.length, owner?.length, currencies?.length]);

  useEffect(() => {
    setPinnedListLoading(true);
    trackActivity('Page Viewed', { page_title: 'Dashboard' })
    globalGetService(getPinnedGraphsApi())
      .then((response) => {
        if (checkApiStatus(response)) {
          setPinnedByCategory(normalizePinnedByCategory(response?.data?.data));
        } else {
          setPinnedByCategory({});
        }
      })
      .catch(() => {
        setPinnedByCategory({});
      })
      .finally(() => {
        setPinnedListLoading(false);
      });
  }, []);

  const pinsFor = (categoryKey) => pinnedByCategory[categoryKey] ?? [];

  return (<>
      <DeploymentBanner />
    <Box className="fleet-dashboard">
      {pinnedListLoading ? <PageLoader /> : null}

      {/* ── Page Header ── */}
      <Box className="fleet-dashboard__header">
        <Box className="fleet-dashboard__header-glow--top" />
        <Box className="fleet-dashboard__header-glow--bottom" />
        <Box className="fleet-dashboard__header-icon">
          <SpaceDashboardIcon className="fleet-dashboard__header-icon-glyph" />
        </Box>
        <Box className="fleet-dashboard__header-text">
          <div className="fleet-dashboard__header-title">
            Fleet Dashboard
          </div>
          <div className="fleet-dashboard__header-subtitle">
            Portfolio performance &amp; pinned insights
          </div>
        </Box>
      </Box>

      {/* ── Section Toggle Bar ── */}
      {/* <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '11px', fontWeight: 600, color: '#8E97A8',
          letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: '4px',
        }}>
          Sections:
        </span>
        {SECTIONS.map(s => (
          <TogglePill
            key={s.key}
            label={s.label}
            active={visible[s.key]}
            onClick={() => toggle(s.key)}
          />
        ))}
      </Box> */}

      {/* ── Sections ── */}
      {visible.portfolio && <PortfolioOverview />}
      {visible.utilization && (
        <PinnedModuleSection
          moduleTitle={MODULE_UTILIZATION}
          // subtitle="Monthly · Last 12 months"
          entries={pinsFor(MODULE_UTILIZATION)}
          onUnpin={handleUnpin}
          loading={pinnedListLoading}
          analyticsFilterMenu={analyticsFilterMenu}
        />
      )}
      {visible.revenue && (
        <PinnedModuleSection
          moduleTitle={MODULE_REVENUE}
          // subtitle="YTD 2025"
          entries={pinsFor(MODULE_REVENUE)}
          onUnpin={handleUnpin}
          loading={pinnedListLoading}
          analyticsFilterMenu={analyticsFilterMenu}
        />
      )}
      {visible.expiry && (
        <PinnedModuleSection
          moduleTitle={MODULE_EXPIRY}
          // subtitle="Next 24 months · sorted by expiry date"
          entries={pinsFor(MODULE_EXPIRY)}
          onUnpin={handleUnpin}
          loading={pinnedListLoading}
          analyticsFilterMenu={analyticsFilterMenu}
        />
      )}
      {visible.maintenance && <PinnedModuleSection
        moduleTitle={MODULE_MAINTENANCE}
        subtitle="Shop visits · reserves · claims "
        entries={pinsFor(MODULE_MAINTENANCE)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {visible.lessee && <PinnedModuleSection
        moduleTitle={MODULE_LESSEE_RISK}
        subtitle="Payment delays · overdue invoices · Composite risk scoring ."
        entries={pinsFor(MODULE_LESSEE_RISK)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.credit      && <CreditInsurance />} */}
      {visible.credit && <PinnedModuleSection
        moduleTitle={MODULE_CREDIT}
        subtitle="insurance status"
        entries={pinsFor(MODULE_CREDIT)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.compliance  && <CompliancePanel />} */}
      {visible.compliance && <PinnedModuleSection
        moduleTitle={MODULE_COMPLIANCE}
        subtitle="Contractual obligations · compliance status"
        entries={pinsFor(MODULE_COMPLIANCE)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.returncond  && <ReturnCondition />} */}
      {/* {visible.options     && <LeaseOptions />} */}
      {visible.options && <PinnedModuleSection
        moduleTitle={MODULE_OPTIONS}
        subtitle="Extension · purchase · return options pipeline"
        entries={pinsFor(MODULE_OPTIONS)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.escalation && <MREscalation />} */}
      {visible.escalation && <PinnedModuleSection
        moduleTitle={MODULE_ESCALATION}
        subtitle="Upcoming rate escalations · revenue uplift · trigger dates"
        entries={pinsFor(MODULE_ESCALATION)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.svforecast  && <ShopVisitForecast />} */}
      {/* {visible.invoicecal  && <InvoiceCalendar />} */}
      {visible.invoicecal && <PinnedModuleSection
        moduleTitle={MODULE_INVOICE_CAL}
        subtitle="Upcoming invoice generation schedule"
        entries={pinsFor(MODULE_INVOICE_CAL)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.remarketing && <RemarketingPipeline />} */}
      {/* {visible.alerts && <CustomAlerts />} */}
      {visible.alerts && <PinnedModuleSection
        moduleTitle={MODULE_ALERTS}
        subtitle="User-configured threshold alerts · portfolio-wide monitoring"
        entries={pinsFor(MODULE_ALERTS)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {visible.utilization_tracking && <PinnedModuleSection
        moduleTitle={MODULE_UTILIZATION_TRACKING}
        entries={pinsFor(MODULE_UTILIZATION_TRACKING)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}
      {/* {visible.sdtrigger   && <DepositTriggers />} */}
      {visible.portfolioCreditOps && <PinnedModuleSection
        moduleTitle={MODULE_PORTFOLIO_CREDIT_OPS}
        subtitle="Lessee revenue concentration · obligation status · sub-lease & wet-lease exposure"
        entries={pinsFor(MODULE_PORTFOLIO_CREDIT_OPS)}
        onUnpin={handleUnpin}
        loading={pinnedListLoading}
        analyticsFilterMenu={analyticsFilterMenu}
      />}

    </Box>
    </>
  );
};

export default FleetDashboard;
